import scrypt from 'scrypt-js'
import aes    from 'aes-js'
import axios  from 'axios'

class TxController {
    constructor() {
        this.CHUNK_SIZE = 1024 * 1024
    }

    async __req(path) {
        let body = await fetch('/api/files/list', {
            credentials: 'include'
        })

        if (body.status !== 200) {
            throw new Error('unauthorized')
        }

        return await body.json()
    }

    async download_profile() {
        let prof = await fetch('/api/profile', {
            credentials: 'include'
        })

        this.prof = await prof.json()
        return this.prof
    }

    /* --- CRYPTO --- */

    async crypto_pbkdf(password, file_name, cb) {
        const to_arr = str => new TextEncoder().encode(str.normalize('NFKC'))
        // perform pbkdf creation
        const key = await scrypt.scrypt(
            to_arr(password),       // password (normalized)
            to_arr(file_name),      // salt
            1024, // N (CPU/mem cost)
            8,    // r (rounds)
            1,    // p (parallelization cost)
            32,   // derived key length (256 bits)
            progress => cb(progress)
        )

        return key
    }

    /* --- FILE UPLOAD --- */

    async file_listing() {
        const list = await this.__req('/api/files/list')
        return list
    }

    async file_details(file_id) {
        return await this.__req(`/api/files/detail/${file_id}`)
    }

    async file_hash(buffer) {
        const hash_buf = await crypto.subtle.digest('SHA-256', buffer)

        return [...new Uint8Array(hash_buf)]
            .map( b => b.toString(16).padStart(2, '0') )
            .join('')
    }

    async file_upload(file, password, note, progress_cb) {
        // patch progress callback
        progress_cb = progress_cb ?? (() => {})

        note = note ?? ''

        // helper: wait a frame
        const wait_frame = () => new Promise((resolve,) => requestAnimationFrame(resolve))

        // keygen
        const key = await this.crypto_pbkdf(
            password,
            file.name,
            progress => progress_cb({mode: 'keyderive', progress})
        )

        // key has been generated, next encrypt our blob with AES
        const aes_cipher = new aes.ModeOfOperation.ctr(key)
        
        progress_cb({mode: 'aes', progress: 0})
        const file_buffer = await file.arrayBuffer()
        const encrypted_bits = new Uint8Array(file_buffer.byteLength)

        for (let i = 0; i < file_buffer.byteLength; i += this.CHUNK_SIZE) {
            encrypted_bits.set(
                await aes_cipher.encrypt(
                    new Uint8Array(file_buffer.slice(i, i+this.CHUNK_SIZE))
                ),
                i
            )
            progress_cb({mode: 'aes', progress: i / file_buffer.byteLength})

            await wait_frame()
        }

        const file_hash = await this.file_hash(file_buffer)

        progress_cb({mode: 'aes', progress: 1})

        const response = await axios.request({
            method: 'post', 
            url: '/api/files/upload', 
            data: encrypted_bits.buffer,
            headers: {
                'X-Mime-Type': file.type,
                'X-Original-Length': file.size,
                'X-Original-Hash': file_hash,
                'X-Filename': file.name,
                'X-Note': encodeURIComponent(note),
                'Content-Type': 'application/octet-stream'
            },
            onUploadProgress: (p) => {
                progress_cb({
                    mode: 'upload',
                    progress: p.loaded / p.total
                })
            }
        })        

        return response.data
    }

    async file_download(file, password, progress_cb) {
        // patch progress cb
        progress_cb = progress_cb ?? (() => {})

        // helper: wait a frame
        const wait_frame = () => new Promise((resolve,) => requestAnimationFrame(resolve))

        // check that the server says this exists
        if (!file.exists) {
            progress_cb({mode: 'error', msg: `Server says the file doesn't exist!`})
            return
        }


        // download
        const response = await axios.request({
            method: 'get', 
            url: `/api/files/download/${file.file.file_id}/content`,
            responseType: 'blob',
            onDownloadProgress: (p) => {
                progress_cb({
                    mode: 'download',
                    progress: p.loaded / p.total
                })
            }
        })

        const dl_blob = await response.data
        const encrypted = await dl_blob.arrayBuffer()

        // keygen
        const key = await this.crypto_pbkdf(
            password,
            file.file.file_name,
            progress => progress_cb({mode: 'keyderive', progress})
        )


        // key has been generated, next decrypt our blob with AES
        const aes_cipher = new aes.ModeOfOperation.ctr(key)
        
        progress_cb({mode: 'aes', progress: 0})
        const decrypted_bits = new Uint8Array(encrypted.byteLength)

        for (let i = 0; i < encrypted.byteLength; i += this.CHUNK_SIZE) {
            decrypted_bits.set(
                await aes_cipher.decrypt(
                    new Uint8Array(encrypted.slice(i, i+this.CHUNK_SIZE))
                ),
                i
            )
            progress_cb({mode: 'aes', progress: i / encrypted.byteLength})

            await wait_frame()
        }


        // check the sig
        progress_cb({mode: 'hash', progress: 0})
        const hash = await this.file_hash(decrypted_bits.buffer)


        if (hash === file.file.file_hash) {
            progress_cb({mode: 'hash', progress: 1})
        } else {
            progress_cb({mode: 'error', msg: 'Password incorrect!'})
            return
        }
        progress_cb({mode: 'hash', progress: 1})

        // everything's done, set up a blob
        
        return new Blob([decrypted_bits.buffer], {type: file.file.mime_type})
    }
}

export default TxController
